import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

// Archiving hides a module from course module lists; nothing is deleted and
// enrolled learners keep access (lms_backend docs/api/module-archive.md).
const BASE = '/module-archive';

export const useArchivedModules = (courseId, enabled) =>
  useQuery(
    ['module-archive', courseId],
    async () =>
      (await axiosInstancev2.get(BASE, { params: { course_id: courseId } }))
        .data?.modules ?? [],
    {
      enabled: Boolean(courseId && enabled),
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

export const useModuleUsage = (moduleId) =>
  useQuery(
    ['module-archive', 'module', moduleId],
    async () => (await axiosInstancev2.get(`${BASE}/${moduleId}`)).data,
    {
      enabled: Boolean(moduleId),
      refetchOnWindowFocus: false,
      retry: false,
      // Counts change as learners join; read them fresh for each dialog.
      cacheTime: 0,
    }
  );

export const useSetArchived = (courseId) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ moduleId, archive }) =>
      (archive
        ? await axiosInstancev2.put(`${BASE}/${moduleId}`)
        : await axiosInstancev2.delete(`${BASE}/${moduleId}`)
      ).data,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['course-modules', courseId]);
        queryClient.invalidateQueries(['module-archive', courseId]);
      },
    }
  );
};

// A 404 without a body means the archive API isn't deployed.
export const archiveUnavailable = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  return status === 404 && !(data?.message || data?.error);
};

export const archiveError = (error, fallback) => {
  if (archiveUnavailable(error)) {
    return "Archiving modules isn't available on the server yet.";
  }
  const data = error?.response?.data;
  return data?.message || data?.error || fallback;
};
